import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { createPartner } from 'submission/lib/partner-api'
import type { Partner, PartnerWriteBody, Tier } from './types'
import { HANDLE_RE, TIERS } from './types'

type Props = {
  open: boolean
  onClose: () => void
  onCreated: (p: Partner) => void
}

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'

const CreatePartnerModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { showToast } = useToast()
  const [privyId, setPrivyId] = useState('')
  const [handle, setHandle] = useState('')
  const [tierOverride, setTierOverride] = useState<Tier | ''>('')
  const [payoutWallet, setPayoutWallet] = useState('')
  const [payoutChainId, setPayoutChainId] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setPrivyId('')
    setHandle('')
    setTierOverride('')
    setPayoutWallet('')
    setPayoutChainId('')
    setNotes('')
    setError(null)
  }

  const valid = privyId.trim().length > 0 && HANDLE_RE.test(handle.trim())

  const submit = async () => {
    if (!valid) return
    setSubmitting(true)
    setError(null)
    const body: PartnerWriteBody = {
      privy_id: privyId.trim(),
      handle: handle.trim(),
    }
    if (tierOverride) body.tier_override = tierOverride
    if (payoutWallet.trim()) body.payout_wallet = payoutWallet.trim()
    if (payoutChainId.trim()) body.payout_chain_id = Number(payoutChainId)
    if (notes.trim()) body.notes = notes.trim()
    try {
      const created = await createPartner(body)
      showToast({
        type: 'success',
        message: `Partner ${created.handle} created`,
      })
      reset()
      onCreated(created)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New partner</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm">
            Privy ID
            <input
              className={inputCls}
              value={privyId}
              onChange={(e) => setPrivyId(e.target.value)}
              placeholder="did:privy:…"
            />
          </label>
          <label className="block text-sm">
            Handle (3–32 letters, digits, _ or -)
            <input
              className={inputCls}
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Tier override
            <select
              className={inputCls}
              value={tierOverride}
              onChange={(e) => setTierOverride(e.target.value as Tier | '')}
            >
              <option value="">None (computed)</option>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Payout wallet
            <input
              className={inputCls}
              value={payoutWallet}
              onChange={(e) => setPayoutWallet(e.target.value)}
              placeholder="0x…"
            />
          </label>
          <label className="block text-sm">
            Payout chain ID
            <input
              className={inputCls}
              type="number"
              value={payoutChainId}
              onChange={(e) => setPayoutChainId(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Notes
            <textarea
              className={inputCls}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || submitting}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreatePartnerModal
